/**
 * Saccades — ballistic gaze shifts between targets.
 *
 * The second exercise, and the one that tests the architecture (build prompt
 * §1). Adding it changed exactly two files: this one, and one import plus one
 * `register()` line in ../index.ts. No form, no chart, no route, no migration,
 * no change to the preview screen, no change to the server.
 *
 * WHY THIS IS A SEPARATE MODULE AND NOT A PURSUIT PARAMETER
 * ---------------------------------------------------------
 * docs/ADDING_AN_EXERCISE.md sets the test: a new module is justified when the
 * TASK LOGIC differs — what the patient is asked to do, or how the response is
 * scored — not when the geometry or the speed differs. Saccades clear that bar
 * comfortably:
 *
 *   Pursuit  a target moves continuously; the patient tracks it; error is
 *            sampled continuously and averaged over epochs.
 *   Saccades a target jumps and stays still; the patient acquires it and
 *            responds; each jump is a discrete trial with its own latency
 *            and a right-or-wrong outcome.
 *
 * Different task, different movement. A horizontal saccade task is not
 * "smooth pursuit at infinite speed".
 *
 * THE PATIENT DOES NOTHING WITH THEIR HANDS — changed 2026-08-23.
 *
 * Until now the patient clicked on each target as it landed, and the latency
 * and landing were scored. That is gone, here and in all three other
 * oculomotor exercises. The objection was not that the number was impure but
 * that asking for the click CHANGED WHAT THE EYES WERE DOING: it imposed a
 * reaction-time task on top of a gaze task, put the patient in "answer fast"
 * mode rather than "look accurately" mode, and in tandem stance — a prescribed
 * progression — added a motor demand while they were already unsteady.
 *
 * The exercise is now unverifiable, and that is accepted: the platform trains
 * and tracks, it does not assess (§11). The stimulus still does the work — a
 * patient looking at the screen makes a saccade to a target that jumps, by
 * reflex. Compliance and the symptom rating the patient already taps between
 * exercises carry the record. See DECISIONS.md 2026-08-23.
 *
 * WHAT IS DELIBERATELY SHARED WITH PURSUIT ANYWAY
 * -----------------------------------------------
 * Target size, target colour, crosshair, background colour and texture, and
 * the position/input-mode declarations all follow the same shape as pursuit.
 * They are not imported from it — each module owns its own declarations, which
 * is what keeps a module a self-contained cartridge — but the doctor meets the
 * same controls, with the same names and the same clinical meanings, on both.
 */
import { degToPx } from '../types.js';
// =============================================================================
// Clinical colour choices — same palettes the doctor already knows from
// pursuit. Declared here rather than imported: a cartridge owns its own
// parameter surface, so it can diverge later without disturbing anything else.
// =============================================================================
const TARGET_COLOURS = [
    { value: '#f0f0f0', label: 'White' },
    { value: '#e02020', label: 'Red' },
    { value: '#2f6fe0', label: 'Blue' },
    { value: '#24a148', label: 'Green' },
    { value: '#f07800', label: 'Orange' },
    { value: '#8a3ffc', label: 'Purple' },
    { value: '#000000', label: 'Black' },
];
const BACKGROUND_COLOURS = [
    { value: '#14161a', label: 'Dark (low stimulus)' },
    { value: '#000000', label: 'Black' },
    { value: '#808080', label: 'Grey' },
    { value: '#ffffff', label: 'White' },
    { value: '#b00020', label: 'Red' },
    { value: '#1a3f8f', label: 'Blue' },
    { value: '#e8c400', label: 'Yellow' },
    { value: '#5b2a86', label: 'Purple' },
];
const CROSSHAIR_COMPLEMENT = {
    '#f0f0f0': '#e02020',
    '#e02020': '#f0f0f0',
    '#2f6fe0': '#f0b000',
    '#24a148': '#e0208a',
    '#f07800': '#2f6fe0',
    '#8a3ffc': '#c8e020',
    '#000000': '#f0f0f0',
};
const PATTERNS = {
    horizontal: { closed: false, from: { x: -1, y: 0 }, to: { x: 1, y: 0 } },
    vertical: { closed: false, from: { x: 0, y: -1 }, to: { x: 0, y: 1 } },
    diagonal_up: {
        closed: false,
        from: { x: -1, y: -1 }, // lower left  -> upper right
        to: { x: 1, y: 1 },
        fitAspect: true,
        mirrorOnReverse: true,
    },
    diagonal_down: {
        closed: false,
        from: { x: -1, y: 1 }, // upper left  -> lower right
        to: { x: 1, y: -1 },
        fitAspect: true,
        mirrorOnReverse: true,
    },
    cross: {
        closed: true,
        points: [
            { x: 0, y: 1 }, // top    -- stepping forward from here
            { x: 1, y: 0 }, // right  -- runs clockwise
            { x: 0, y: -1 }, // bottom
            { x: -1, y: 0 }, // left
        ],
    },
    box: {
        closed: true,
        points: [
            { x: -1, y: 1 }, // upper left  -- stepping forward from here
            { x: 1, y: 1 }, // upper right -- runs clockwise
            { x: 1, y: -1 }, // lower right
            { x: -1, y: -1 }, // lower left
        ],
    },
};
/**
 * Lay `stops` stations along a line pattern, evenly spaced, with one at each
 * end. Three stations across a horizontal path puts targets at the left edge,
 * the centre and the right edge — two equal saccades per pass.
 */
function positionsFor(spec, stops, verticalScale = 1) {
    if (spec.points)
        return spec.points;
    const from = spec.from;
    const to = spec.to;
    const count = Math.max(2, Math.round(stops));
    const out = [];
    for (let i = 0; i < count; i += 1) {
        const t = i / (count - 1);
        out.push({
            x: from.x + (to.x - from.x) * t,
            y: (from.y + (to.y - from.y) * t) * verticalScale,
        });
    }
    return out;
}
/**
 * Angular gap between neighbouring stations, in degrees.
 *
 * Used for validation only, so it assumes a 16:9 canvas for the aspect-fitted
 * diagonals rather than measuring one — the check runs in the prescription
 * builder, where no canvas exists yet. It is a lower bound on a wider screen.
 */
const ASSUMED_ASPECT = 9 / 16;
function stationSpacingDeg(patternKey, amplitudeDeg, stops) {
    const spec = PATTERNS[patternKey];
    if (!spec)
        return amplitudeDeg * 2;
    if (spec.points) {
        // Ring patterns: neighbouring points are a fixed distance apart.
        const a = spec.points[0];
        const b = spec.points[1];
        return Math.hypot(b.x - a.x, b.y - a.y) * amplitudeDeg;
    }
    const yScale = spec.fitAspect ? ASSUMED_ASPECT : 1;
    const from = spec.from;
    const to = spec.to;
    const span = Math.hypot(to.x - from.x, (to.y - from.y) * yScale) * amplitudeDeg;
    return span / (Math.max(2, Math.round(stops)) - 1);
}
export const saccades = {
    definition: {
        id: 'saccades',
        // Bumped to 2 on 2026-08-23 when the click was removed. Results recorded
        // under version 1 carry reaction time, accuracy and variability; from
        // version 2 there is only completion. The tag is what stops a chart
        // showing that as a patient falling off a cliff.
        version: 2,
        name: 'Saccades',
        category: 'oculomotor',
        clinicalDescription: 'Ballistic gaze shifts between fixed targets. The target jumps to a new ' +
            'position and waits; the patient acquires it with their eyes. Horizontal, ' +
            'vertical, diagonal, four-point cross and four-corner box patterns are ' +
            'parameter settings. Predictable alternation trains anticipation; random ' +
            'placement and random timing remove it. The patient makes no response — ' +
            'nothing is clicked — so this trains and is not scored.',
        patientInstruction: 'A dot will jump around the screen. As soon as it moves, look at its new ' +
            'position. Move your eyes, not your head. There is nothing to click. If ' +
            'symptoms start to build, press the spacebar to stop — stopping early is ' +
            'the right call.',
        params: {
            pattern: {
                kind: 'enum',
                label: 'Pattern',
                help: 'Where the target is allowed to appear.',
                options: [
                    { value: 'horizontal', label: 'Horizontal' },
                    { value: 'vertical', label: 'Vertical' },
                    { value: 'diagonal_up', label: 'Diagonal — rising' },
                    { value: 'diagonal_down', label: 'Diagonal — falling' },
                    { value: 'cross', label: 'Cross — four points' },
                    { value: 'box', label: 'Box — four corners' },
                ],
                default: 'horizontal',
            },
            travel: {
                kind: 'enum',
                label: 'Travel',
                help: 'One way steps round the pattern in a single rotational direction. ' +
                    'Back and forth runs to the end of the sequence and comes back. On a ' +
                    'two-point pattern the target must alternate either way, so the ' +
                    'setting shows its effect on the cross and box patterns.',
                options: [
                    { value: 'one_way', label: 'One way' },
                    { value: 'back_and_forth', label: 'Back and forth' },
                ],
                default: 'back_and_forth',
            },
            travelDirection: {
                kind: 'enum',
                label: 'Which way',
                help: 'Which way the sequence runs. On a vertical pattern this reads as ' +
                    'upward or downward. Ignored while Unpredictable position is on, ' +
                    'since that places the target at random.',
                options: [
                    { value: 'forward', label: 'Rightward / clockwise' },
                    { value: 'reverse', label: 'Leftward / anticlockwise' },
                ],
                default: 'forward',
            },
            amplitudeDeg: {
                kind: 'number',
                label: 'Amplitude',
                help: 'Distance of each target from centre, in degrees of visual angle. ' +
                    'Larger amplitudes demand bigger gaze shifts and tire faster.',
                min: 2,
                max: 25,
                step: 1,
                default: 13,
                unit: 'deg',
                fromCentre: true,
            },
            stops: {
                kind: 'number',
                label: 'Stops per pass',
                help: 'How many places the target lands on its way across, spaced equally ' +
                    'with one at each end. Two is a single jump edge to edge. Three adds ' +
                    'a centre stop, so each pass is two equal saccades; five makes four ' +
                    'smaller ones. Applies to the horizontal, vertical and diagonal ' +
                    'patterns — the cross and box carry their own fixed points.',
                min: 2,
                max: 9,
                step: 1,
                default: 5,
                unit: 'count',
            },
            durationSec: {
                kind: 'number',
                label: 'Duration',
                help: 'How long the exercise runs. The target keeps stepping through the ' +
                    'pattern until the time is up, so the number of saccades follows from ' +
                    'this and the time on target rather than being set separately.',
                min: 30,
                max: 600,
                step: 30,
                default: 60,
                unit: 's',
                displayUnit: { unit: 'min', factor: 60 },
            },
            holdSec: {
                kind: 'number',
                label: 'Time on target',
                help: 'How long the target waits before jumping on regardless of response. ' +
                    'Shorter is more demanding.',
                min: 0.5,
                max: 6,
                step: 0.25,
                default: 1,
                unit: 's',
            },
            randomPosition: {
                kind: 'boolean',
                label: 'Unpredictable position',
                help: 'Places each target at random within the pattern rather than ' +
                    'alternating. Removes anticipation — the patient cannot pre-plan the ' +
                    'movement. Harder.',
                default: false,
            },
            randomTiming: {
                kind: 'boolean',
                label: 'Unpredictable timing',
                help: 'Varies the gap before each jump so the moment cannot be anticipated. ' +
                    'Isolates genuine reaction time.',
                default: false,
            },
            targetSizeDeg: {
                kind: 'number',
                label: 'Target size',
                help: 'Diameter in degrees of visual angle. Also sets how precisely the ' +
                    'patient must land to be scored correct.',
                min: 0.5,
                max: 4,
                step: 0.5,
                default: 1,
                unit: 'deg',
            },
            targetColor: {
                kind: 'enum',
                label: 'Target colour',
                help: 'A clinical parameter, not a branding choice. Lower contrast against ' +
                    'the background for photophobic patients.',
                options: TARGET_COLOURS,
                default: '#f0f0f0',
            },
            crosshair: {
                kind: 'boolean',
                label: 'Crosshair on target',
                help: 'Draws fine lines inside the target to sharpen the landing point.',
                default: true,
            },
            backgroundColor: {
                kind: 'enum',
                label: 'Background',
                help: 'The exercise canvas. Darker surfaces are lower stimulus for ' +
                    'photophobic patients; white is the brightest.',
                options: BACKGROUND_COLOURS,
                default: '#808080',
            },
            backgroundTexture: {
                kind: 'boolean',
                label: 'Textured background',
                help: 'Adds a faint depth texture behind the target.',
                default: true,
            },
        },
        /**
         * Completion only. With no patient response there is nothing else this
         * exercise can honestly report — see the header.
         */
        metrics: ['completion_pct'],
        decay: [],
        decayNotMonitored: 'Takes no patient response, so it emits no performance metric for the ' +
            'monitor to read. Clicking each target imposed a reaction-time task on ' +
            'top of a gaze task and changed what the patient’s eyes were doing, ' +
            'which is the therapy. Symptoms and compliance carry the record. ' +
            'Changed 2026-08-23.',
        allowedPositions: ['seated', 'standing', 'tandem'],
        /** Empty: the patient watches. The only control is the host's stop. */
        allowedInputModes: [],
        involvesHeadMovement: false,
        involvesEyesClosed: false,
        /** The parameter the safety system shortens when reducing a dose. */
        durationParam: 'durationSec',
        estimateDurationSec(params) {
            return Math.round(Number(params.durationSec) + 10); // + instruction screen
        },
        validate(params) {
            const messages = [];
            const amplitude = Number(params.amplitudeDeg);
            const targetSize = Number(params.targetSizeDeg);
            // Check the gap between NEIGHBOURING stations, not the full amplitude.
            // Adding stops divides the same span into smaller hops, so a target size
            // that is fine edge-to-edge can still overlap once stations are added.
            const spacing = stationSpacingDeg(String(params.pattern), amplitude, Number(params.stops));
            // Blocking: overlapping targets are not two stations, they are one blob,
            // and nothing the patient does between them is a measurable gaze shift.
            if (targetSize >= spacing) {
                messages.push({
                    severity: 'blocking',
                    param: 'targetSizeDeg',
                    message: `Targets would overlap: neighbouring stops are ${spacing.toFixed(1)} degrees ` +
                        `apart and the target is ${targetSize.toFixed(1)} degrees across. Reduce the ` +
                        'target size, reduce the stops per pass, or increase the amplitude.',
                });
            }
            return messages;
        },
    },
    // ===========================================================================
    // The run half — browser only. Nothing above this line touches the DOM.
    // ===========================================================================
    run(ctx) {
        const { params, canvas } = ctx;
        const requestedAmplitudeDeg = Number(params.amplitudeDeg);
        const durationSec = Number(params.durationSec);
        const holdSec = Number(params.holdSec);
        const targetSizeDeg = Number(params.targetSizeDeg);
        const targetColor = String(params.targetColor);
        const crosshairColor = CROSSHAIR_COMPLEMENT[targetColor] ?? '#e02020';
        const backgroundColor = String(params.backgroundColor);
        const crosshair = params.crosshair === true;
        const textured = params.backgroundTexture === true;
        const randomPosition = params.randomPosition === true;
        const randomTiming = params.randomTiming === true;
        const pingPong = String(params.travel) === 'back_and_forth';
        /** +1 steps forward through the position list, which runs clockwise. */
        const directionStep = String(params.travelDirection) === 'reverse' ? -1 : 1;
        const patternKey = String(params.pattern);
        const patternSpec = PATTERNS[patternKey] ?? PATTERNS.horizontal;
        const closedRing = patternSpec.closed;
        // Unit positions before the canvas is known. resolveGeometry() replaces
        // these once the aspect ratio can be measured.
        let offsets = positionsFor(patternSpec, Number(params.stops));
        const surface = canvas.getContext('2d');
        let amplitudeDeg = requestedAmplitudeDeg;
        let texture = null;
        // Cached canvas box. Never measure the DOM inside the draw loop — see
        // DECISIONS.md 2026-08-20 on why that shows up as visible judder.
        let box = { width: 0, height: 0 };
        let devicePixelScale = 1;
        let resizeObserver = null;
        function measure() {
            const rect = canvas.getBoundingClientRect();
            box = { width: rect.width, height: rect.height };
            devicePixelScale = rect.width > 0 ? canvas.width / rect.width : 1;
        }
        /**
         * Lay out the stations against the real canvas, then fit the amplitude so
         * no target can land off screen.
         *
         * The diagonals stretch vertically to the canvas aspect ratio here, which
         * is what makes them run corner to corner rather than at a fixed 45
         * degrees. At maximum amplitude they reach the actual corners.
         */
        function resolveGeometry() {
            measure();
            const radiusPx = degToPx(targetSizeDeg, ctx.pxPerDegree) / 2;
            const aspect = box.width > 0 ? box.height / box.width : 1;
            // Mirror the diagonals when running leftward, so a falling pattern still
            // falls rather than becoming a rising one under a falling label.
            const mirror = patternSpec.mirrorOnReverse && directionStep === -1 ? -1 : 1;
            offsets = positionsFor(patternSpec, Number(params.stops), (patternSpec.fitAspect ? aspect : 1) * mirror);
            // How far the furthest station sits on each axis, in amplitude units.
            // An axis nothing travels on imposes no limit.
            const reachX = Math.max(...offsets.map((o) => Math.abs(o.x)));
            const reachY = Math.max(...offsets.map((o) => Math.abs(o.y)));
            const maxAmplitudeFor = (reach, halfPx) => reach > 0 ? Math.max(halfPx - radiusPx, 0) / ctx.pxPerDegree / reach : Infinity;
            amplitudeDeg = Math.min(requestedAmplitudeDeg, maxAmplitudeFor(reachX, box.width / 2), maxAmplitudeFor(reachY, box.height / 2));
            // What this screen can actually show, which is not always what was
            // prescribed. Reported on every resolve, including after a resize, so the
            // record describes the run rather than the intention.
            ctx.reportDelivered({ amplitudeDeg: Math.round(amplitudeDeg * 100) / 100 });
        }
        function buildTexture() {
            if (!surface || !textured)
                return null;
            const tile = document.createElement('canvas');
            tile.width = 64;
            tile.height = 64;
            const tileSurface = tile.getContext('2d');
            if (!tileSurface)
                return null;
            tileSurface.fillStyle = backgroundColor;
            tileSurface.fillRect(0, 0, 64, 64);
            tileSurface.fillStyle = 'rgba(128,128,128,0.14)';
            for (let x = 0; x < 64; x += 8) {
                for (let y = 0; y < 64; y += 8) {
                    const jitterX = ((x * 7 + y * 13) % 5) - 2;
                    const jitterY = ((x * 11 + y * 5) % 5) - 2;
                    tileSurface.beginPath();
                    tileSurface.arc(x + jitterX, y + jitterY, 1.2, 0, Math.PI * 2);
                    tileSurface.fill();
                }
            }
            return surface.createPattern(tile, 'repeat');
        }
        let frame = 0;
        let stopped = false;
        let lastFrameAt = 0;
        let runningSec = 0;
        let trialIndex = 0;
        /** Start at whichever end the chosen direction runs FROM. */
        let offsetIndex = directionStep === 1 ? 0 : offsets.length - 1;
        /** False until the first target has been put on screen. */
        let presented = false;
        /** When the current target should jump on. */
        let expiresAt = 0;
        /** Current rotational sense. Flips each lap when Back and forth is on. */
        let sweepStep = directionStep;
        function nextOffset() {
            if (randomPosition) {
                // Never repeat the same position twice — a saccade needs somewhere to go.
                let next = offsetIndex;
                if (offsets.length > 1) {
                    while (next === offsetIndex)
                        next = Math.floor(Math.random() * offsets.length);
                }
                offsetIndex = next;
                return offsets[offsetIndex];
            }
            const count = offsets.length;
            if (!closedRing && pingPong && count > 2) {
                // A line with stations along it bounces at the two ends: left, centre,
                // right, centre, left. It does not wrap round.
                const ahead = offsetIndex + sweepStep;
                if (ahead >= count || ahead < 0)
                    sweepStep = (-sweepStep);
                offsetIndex += sweepStep;
                return offsets[offsetIndex];
            }
            // Otherwise step round, wrapping at the ends. On a line running one way
            // the wrap IS the return jump back to the starting edge; on a ring it is
            // simply the next lap.
            offsetIndex = (offsetIndex + sweepStep + count) % count;
            // Back and forth on a ring reverses the whole lap on returning to the
            // start, so a box goes round clockwise then round anticlockwise —
            // matching what the same setting does to a circle in smooth pursuit. It
            // does not retrace a single edge.
            if (closedRing && pingPong && count > 2 && offsetIndex === 0) {
                sweepStep = (-sweepStep);
            }
            return offsets[offsetIndex];
        }
        let current = offsets[0];
        function presentNextTarget() {
            // The very first target is the starting station itself, not a step on
            // from it — a left-to-right pass must begin at the left edge.
            current = presented ? nextOffset() : offsets[offsetIndex];
            presented = true;
            trialIndex += 1;
            const extra = randomTiming ? Math.random() * holdSec : 0;
            expiresAt = runningSec + holdSec + extra;
        }
        function targetCentre() {
            return {
                x: box.width / 2 + degToPx(current.x * amplitudeDeg, ctx.pxPerDegree),
                y: box.height / 2 - degToPx(current.y * amplitudeDeg, ctx.pxPerDegree),
            };
        }
        /**
         * Completion is the share of the prescribed TIME that was actually run.
         * The exercise is a fixed duration, so this is what "how much of it did
         * the patient do" means — and it stays comparable across runs even though
         * the number of saccades varies with the time on target.
         */
        function emitCompletion() {
            ctx.emit({
                t: runningSec * 1000,
                metric: 'completion_pct',
                value: Math.min(100, (runningSec / durationSec) * 100),
                detail: { saccadesPresented: trialIndex },
            });
        }
        function finishRun(reason) {
            emitCompletion();
            ctx.finish(reason);
        }
        function draw() {
            if (stopped || !surface)
                return;
            const nowMs = ctx.now();
            const deltaSec = Math.min((nowMs - lastFrameAt) / 1000, 0.1);
            lastFrameAt = nowMs;
            runningSec += deltaSec;
            // --- advance the trial ------------------------------------------------
            if (runningSec >= expiresAt) {
                if (runningSec >= durationSec) {
                    finishRun('completed');
                    return;
                }
                presentNextTarget();
            }
            // The clock can also run out mid-target. End on time rather than waiting
            // for the current one to expire — a prescribed minute must be a minute.
            if (runningSec >= durationSec) {
                finishRun('completed');
                return;
            }
            // --- render -----------------------------------------------------------
            surface.setTransform(devicePixelScale, 0, 0, devicePixelScale, 0, 0);
            surface.fillStyle = backgroundColor;
            surface.fillRect(0, 0, box.width, box.height);
            if (texture) {
                surface.fillStyle = texture;
                surface.fillRect(0, 0, box.width, box.height);
            }
            const centre = targetCentre();
            const radiusPx = Math.max(degToPx(targetSizeDeg, ctx.pxPerDegree) / 2, 1);
            surface.beginPath();
            surface.arc(centre.x, centre.y, radiusPx, 0, Math.PI * 2);
            surface.fillStyle = targetColor;
            surface.fill();
            if (crosshair) {
                surface.save();
                surface.beginPath();
                surface.arc(centre.x, centre.y, radiusPx, 0, Math.PI * 2);
                surface.clip();
                surface.strokeStyle = crosshairColor;
                surface.lineWidth = Math.max(radiusPx / 5, 1);
                surface.beginPath();
                surface.moveTo(centre.x - radiusPx, centre.y);
                surface.lineTo(centre.x + radiusPx, centre.y);
                surface.moveTo(centre.x, centre.y - radiusPx);
                surface.lineTo(centre.x, centre.y + radiusPx);
                surface.stroke();
                surface.restore();
            }
            frame = requestAnimationFrame(draw);
        }
        return {
            start() {
                resolveGeometry();
                texture = buildTexture();
                lastFrameAt = ctx.now();
                if (typeof ResizeObserver !== 'undefined') {
                    resizeObserver = new ResizeObserver(() => measure());
                    resizeObserver.observe(canvas);
                }
                presentNextTarget();
                frame = requestAnimationFrame(draw);
            },
            /** Instant and idempotent (rule 5). May be called twice. */
            stop(reason) {
                if (stopped)
                    return;
                stopped = true;
                cancelAnimationFrame(frame);
                resizeObserver?.disconnect();
                resizeObserver = null;
                if (reason !== 'completed')
                    emitCompletion();
            },
        };
    },
};
//# sourceMappingURL=saccades.js.map